// A dynamic import() of a route/component chunk can fail when the served module
// graph rotates out from under an open tab: in dev, Vite re-optimizes deps and the
// stale ?v= hash returns HTTP 504; in prod, a deploy rotates the hashed chunk
// filename and the old URL 404s. Both surface as the same chunk-load error, and
// re-rendering can't fix it — only a full reload re-fetches the current graph.

import { readSession, writeSession } from '#/lib/storage'

const RELOAD_KEY = 'module-reload:last-attempt'
const RELOAD_DEBOUNCE_MS = 10_000

export function isModuleLoadError(error: unknown): boolean {
  return (
    error instanceof Error &&
    /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed/i.test(
      error.message,
    )
  )
}

// Records a reload attempt and reports whether the caller should reload now. Returns
// false if a reload was already attempted within the debounce window — if the chunk
// is still missing after one reload it's genuinely broken, so we surface the error
// rather than reload-loop.
export function shouldReloadForModuleError(now: number = Date.now()): boolean {
  const stored = readSession(RELOAD_KEY)
  const last = stored === null ? Number.NEGATIVE_INFINITY : Number(stored)
  if (now - last < RELOAD_DEBOUNCE_MS) return false
  // Only reload if the attempt persisted; if storage is unavailable the debounce
  // can't hold, so reloading would loop forever on a genuinely-missing chunk.
  return writeSession(RELOAD_KEY, String(now))
}
